import React, { useState } from "react";

import { patchNode } from "../../core/postNode";

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {
  IconButton,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

export default function MediaNameEditor({
  selectedMedia,
  user,
  csrf_token,
  onNameUpdate,
  label = "File name",
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(selectedMedia?.name || "");
  const [savedName, setSavedName] = useState(selectedMedia?.name || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const currentUid = user?.current_user?.uid;
  const canEdit =
    Boolean(selectedMedia?.is_admin) ||
    (currentUid != null &&
      selectedMedia?.uid != null &&
      String(selectedMedia.uid) === String(currentUid));

  const startEdit = () => {
    setValue(savedName);
    setError(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setValue(savedName);
    setError(null);
    setEditing(false);
  };

  const save = () => {
    const trimmed = value.trim();
    if (!trimmed) {
      setError("Name cannot be empty.");
      return;
    }
    if (trimmed === savedName) {
      setEditing(false);
      return;
    }

    setSaving(true);
    setError(null);

    patchNode(
      `media_library/${selectedMedia.mid}`,
      { mid: selectedMedia.mid, name: trimmed },
      csrf_token,
    )
      .then(() => {
        setSavedName(trimmed);
        setSaving(false);
        setEditing(false);
        if (onNameUpdate) onNameUpdate(trimmed);
      })
      .catch(() => {
        setSaving(false);
        setError("Failed to rename. Please try again.");
      });
  };

  return (
    <Row className="mb-2 align-items-center info-row">
      <Col xs={4} sm={3}>
        <Typography variant="form-group-label" owner="admin">
          {label}
        </Typography>
      </Col>
      <Col className="d-flex align-items-center" style={{ gap: 4 }}>
        {editing && canEdit ? (
          <>
            <TextField
              size="small"
              fullWidth
              autoFocus
              value={value}
              disabled={saving}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  save();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  cancelEdit();
                }
              }}
              error={Boolean(error)}
              helperText={error || ""}
            />
            <IconButton
              size="small"
              color="primary"
              onClick={save}
              disabled={saving}
              aria-label="Save name"
            >
              {saving ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
            </IconButton>
            <IconButton
              size="small"
              onClick={cancelEdit}
              disabled={saving}
              aria-label="Cancel rename"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        ) : (
          <>
            <Typography
              variant="form-group-label"
              owner="admin"
              weight="regular"
              color="adminText.main"
              break="break-all"
            >
              {savedName}
            </Typography>
            {canEdit && (
              <IconButton
                size="small"
                onClick={startEdit}
                aria-label="Rename media"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </>
        )}
      </Col>
    </Row>
  );
}
